import { queryOptions } from "@tanstack/react-query";
import { unwrap } from "@wilco/api";
import type { ComponentType } from "react";
import {
  IconAlert,
  IconClock,
  IconKit,
  IconPencil,
  IconQr,
  IconSend,
  IconShelf,
  IconTruck,
  IconWrench,
  type IconProps,
} from "@wilco/ui";
import { api } from "../auth.ts";
import type { MessageKey } from "../i18n.ts";
import type { Target } from "../shell/targets.tsx";

/*
 * «Робочий стіл»: плитка это задача роли, а не приложение (chmk_shell,
 * models/desk.py, TILES). Счётчик считает документы, которые ждут именно
 * эту роль. GET /api/v1/desk в контракте нет: счётчики склада, закупок и
 * производства считаются по их спискам из REST, «Мої завдання» по
 * /api/v1/tasks/mine, отгрузки по задачам «Відвантажити покупцю»,
 * закрытие месяца по черновикам закрытий. Открытые ремонты сервер списком
 * не отдаёт: плитка без числа.
 */
export type Tone = "primary" | "info" | "warning" | "danger" | "neutral";

export interface Tile {
  key: string;
  title: MessageKey;
  icon: ComponentType<IconProps>;
  /** null у экрана-сканера и у плитки, которую сервер не считает: значок крупно на месте числа. */
  count: number | null;
  tone: Tone;
  /** Куда ведёт: экран склада или раздел, пока своих списков нет. */
  target: Target;
  /** На нижнюю панель телефона, короткой подписью. */
  bottom?: MessageKey;
}

export interface DeskGroup {
  key: string;
  title: MessageKey;
  tiles: Tile[];
}

export interface Desk {
  /** null, если задач сервер не отдал: плитки нет, а не «0». */
  tasks: { count: number; overdue: number; today: number; week: number } | null;
  groups: DeskGroup[];
}

interface Counts {
  receipts: number | null;
  kits: number | null;
  shortage: number | null;
  drafts: number | null;
  incoming: number | null;
  late: number | null;
  mo: number | null;
  ship: number | null;
  closes: number | null;
  tasks: Desk["tasks"];
}

// Списка, который роли не положен (403) или упал, нет: плитка без числа,
// а не ноль, иначе «0» обещал бы, что работы нет.
const settled = async <T>(p: Promise<T>, f: (v: T) => number): Promise<number | null> => {
  try {
    return f(await p);
  } catch {
    return null;
  }
};

async function counts(): Promise<Counts> {
  const orders = (filter: "DRAFTS" | "NOT_RECEIVED" | "LATE") => unwrap(api.GET("/api/v1/purchasing/orders", { params: { query: { filter: [filter] } } }));
  const tasks = unwrap(api.GET("/api/v1/tasks/mine")).then(
    (t) => ({ count: t.overdue.length + t.today.length + t.week.length + t.later.length, overdue: t.overdue.length, today: t.today.length, week: t.week.length }),
    () => null,
  );
  const [receipts, kits, shortage, drafts, incoming, late, mo, ship, closes] = await Promise.all([
    settled(orders("NOT_RECEIVED"), (l) => l.filter((o) => o.state === "CONFIRMED").length),
    settled(unwrap(api.GET("/api/v1/inventory/requests", { params: { query: { status: "QUEUED" } } })), (l) => l.length),
    settled(unwrap(api.GET("/api/v1/purchasing/baskets")), (l) => l.reduce((a, b) => a + b.lines.length, 0)),
    settled(orders("DRAFTS"), (l) => l.length),
    settled(unwrap(api.GET("/api/v1/purchasing/expected")), (l) => l.length),
    settled(orders("LATE"), (l) => l.length),
    settled(unwrap(api.GET("/api/v1/manufacturing/orders")), (l) => l.filter((o) => o.state === "CONFIRMED").length),
    settled(unwrap(api.GET("/api/v1/sales/tasks/ship")), (l) => l.length),
    settled(unwrap(api.GET("/api/v1/costing/closes")), (l) => l.filter((c) => c.state === "DRAFT").length),
  ]);
  return { receipts, kits, shortage, drafts, incoming, late, mo, ship, closes, tasks: await tasks };
}

const desk = (c: Counts): Desk => ({
  tasks: c.tasks,
  groups: [
    {
      key: "stock",
      title: "desk.group.stock",
      tiles: [
        { key: "shelf", title: "desk.tile.shelf", icon: IconShelf, count: null, tone: "primary", target: { kind: "shelf" }, bottom: "bottom.shelf" },
        { key: "receipts", title: "desk.tile.receipts", icon: IconTruck, count: c.receipts, tone: "info", target: { kind: "today", tab: "receive" } },
        { key: "kits", title: "desk.tile.kits", icon: IconKit, count: c.kits, tone: "warning", target: { kind: "today", tab: "issue" }, bottom: "bottom.kits" },
        { key: "deliveries", title: "desk.tile.deliveries", icon: IconSend, count: c.ship, tone: "info", target: { kind: "sales" } },
      ],
    },
    {
      key: "purchase",
      title: "desk.group.purchase",
      tiles: [
        { key: "shortage", title: "desk.tile.shortage", icon: IconAlert, count: c.shortage, tone: "danger", target: { kind: "purchasing", tab: "shortage" }, bottom: "bottom.shortage" },
        { key: "poDraft", title: "desk.tile.poDraft", icon: IconPencil, count: c.drafts, tone: "neutral", target: { kind: "purchasing", tab: "drafts" } },
        { key: "poIncoming", title: "desk.tile.poIncoming", icon: IconTruck, count: c.incoming, tone: "info", target: { kind: "purchasing", tab: "expected" } },
        { key: "poLate", title: "desk.tile.poLate", icon: IconClock, count: c.late, tone: "danger", target: { kind: "purchasing", tab: "expected" } },
      ],
    },
    {
      key: "mrp",
      title: "desk.group.mrp",
      tiles: [
        { key: "product", title: "desk.tile.product", icon: IconQr, count: null, tone: "primary", target: { kind: "unit" } },
        { key: "moOpen", title: "desk.tile.moOpen", icon: IconWrench, count: c.mo, tone: "primary", target: { kind: "line" } },
      ],
    },
    {
      key: "management",
      title: "desk.group.management",
      tiles: [
        { key: "repairs", title: "desk.tile.repairs", icon: IconWrench, count: null, tone: "primary", target: { kind: "unit" } },
        { key: "monthClose", title: "desk.tile.monthClose", icon: IconClock, count: c.closes, tone: "neutral", target: { kind: "costing" } },
      ],
    },
  ],
});

export const deskQuery = queryOptions({
  queryKey: ["desk"],
  queryFn: async (): Promise<Desk> => desk(await counts()),
  // Палитра зовёт стол на каждую букву, как в chmk_shell: кэш 30 с.
  staleTime: 30_000,
});

/** Плитки для нижней панели телефона: до трёх с пометкой bottom. */
export const bottomTiles = (desk: Desk): Tile[] =>
  desk.groups.flatMap((g) => g.tiles).filter((t) => t.bottom).slice(0, 3);
